/**
 * TODO:
 * * Consider updating most/all usages of Table to pass in `undefined` data while the data is loading, which will cause the Table to render as a collection of Skeletons that match the layout of the table and its filters.
 * * Consider replacing the few remaining direct usages of MUITable with Table.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Skeleton } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { type JSX, useMemo, useState } from 'react';

import { Text } from 'Components/Text/Text';
import { ICON_EXPAND_COLLAPSE } from 'Config/Icons';
import { undefinedComparator } from 'Helpers/Compare';
import { ColorTheme, GroupOptionType } from 'Models/Types/GlobalType';

import { ColumnConfig, ColumnDefinition, getColumnDefinitionFromColumnConfig } from './Columns';
import { Filter, FilterConfig, FilterType } from './Components/Filters';
import styles from './Table.module.css';
import { MUITable, MUITableBody, MUITableOverflowCell } from '../MUITable/MUITable';

export type PageVariant = 'standard' | 'dashboard';
const PAGE_VARIANT_DEFAULT: PageVariant = 'standard';

/**
 * Primitive types that can be used as filterable values for table cells.
 * I'm unsure of whether it is a good thing to restrict values to these types. It must be done regardless because of how our (wildly inconsistent) form field components are defined.
 */
export type CellValue = string | number;

export type SortDirection = 'ascending' | 'descending';

const STANDARD_TABLE_HEADER_STYLE = {
    backgroundColor: '#E6EDF1',
    borderTop: '1px solid #dee2e6',
    maxHeight: '30px',
    borderRadius: '9px',
    width: '100%',
} as const;

const DASHBOARD_TABLE_HEADER_STYLE = {
    borderTop: 'hidden',
    maxHeight: '30px',
    borderRadius: '9px',
    width: '100%',
} as const;

/**
 * Configuration for a Table.
 * @typeParam RowData - The shape of each row's data object.
 */
export interface TableProps<RowData> {
    /** The definitions of what and how to display for each column. */
    columns: ColumnConfig<RowData>[];
    /** The name of the column to sort by initially. Must match the `name` of one of the `columns`. */
    defaultSortColumn: string;
    /** The definitions of the filters to display above the table, if any. */
    filters: FilterConfig<RowData>[];
    /** Extracts a unique identifier from each row for React `key`s. */
    getUniqueIdForRow: (data: RowData) => string;
    /** The data to display. If `undefined`, a loading skeleton will be displayed while waiting for the data to load. */
    rowsData: RowData[] | undefined;

    /**
     * Optional explicit ordering of columns for tie breaking during sorting.
     * When two values are considerd equal when sorting by the selected "sort by" column, these columns are used as secondary sorts in order.
     * Each name must match a sortable column. By default, all sortable columns are used as tie breakers in order from left to right.
     */
    columnSubsortOrder?: string[];
    /** The initial sort direction. Defaults to ascending. */
    defaultSortDirection?: SortDirection;
    /**
     * When true, disables all column sorting (headers will not be clickable). The current standard is that any table displaying historical records in chronological order should disable sorting.
     * If true, `defaultSortColumn` may be null. But in almost all cases, there should still be a `defaultSortColumn` (e.g., a table displays unsortable, chronological history: the table is still sorted chronologically by default).
     */
    disableSorting?: boolean;
    /** If provided, renders an additional "details" row below each "main" row, where the "details" row can be expanded and collapsed. The element returned for a row is shown in the "details" row when expanded. */
    getExpandableElementForRow?: (data: RowData) => JSX.Element;
    /** If provided, renders an overflow menu in the last column of each row. */
    getOverflowMenuForRow?: (data: RowData) => JSX.Element;
    /** The variant of the page, which dictates colors. Defaults to standard. */
    pageVariant?: PageVariant;
}

/**
 * A reusable table component with built-in filtering, sorting, and optional row expansion.
 * When sorting, sorting is done according to the sort comparator of the selected "sort by" column and, by default, all sortable columns are used as tie breakers in order from left to right.
 *
 * @typeParam RowData - The shape of each row's data object.
 */
export const Table = <RowData,>(props: TableProps<RowData>): JSX.Element => {
    const { columns, columnSubsortOrder, defaultSortColumn, defaultSortDirection = 'ascending', disableSorting = false, filters, getExpandableElementForRow, getOverflowMenuForRow, getUniqueIdForRow, rowsData, pageVariant = PAGE_VARIANT_DEFAULT } = props;

    const [sortColumnName, setSortColumnName] = useState<string | null>(() => {
        if (defaultSortColumn !== null && columns.find((column) => column.name === defaultSortColumn) === undefined) {
            // To enforce this with TypeScript would greatly complicate typing. Throwing an error that will always be seen during development if the table is defined incorrectly is acceptable.
            throw new Error(`"${defaultSortColumn}" is not a valid column.`);
        }
        return defaultSortColumn;
    });
    const [sortDirection, setSortDirection] = useState<SortDirection>(defaultSortDirection);

    // A mapping of filter labels (assumed to be unique) to selections for the filters. The value type depends on the filter type:
    // - SINGLE_PROPERTY (multiple selections not allowed): CellValue
    // - SINGLE_PROPERTY (multiple selections allowed): CellValue[]
    // - MULTIPLE_PROPERTIES: GroupOptionType[]
    // - USERS: string[] (user IDs)
    // - TAGS: string[] (tag IDs)
    const [filterSelections, setFilterSelections] = useState<Record<string, any>>(() => {
        const initialSelections: Record<string, any> = {};

        // Initialize each filter with its default selections, if provided.
        for (const filter of filters) {
            switch (filter.type) {
                case FilterType.SINGLE_PROPERTY: {
                    if (filter.defaultSelections && filter.defaultSelections.length > 0) {
                        initialSelections[filter.label] = filter.canSelectMultiple ? filter.defaultSelections : filter.defaultSelections[0];
                    }
                    break;
                }
                case FilterType.MULTIPLE_PROPERTIES: {
                    const defaultGroupOptions: GroupOptionType[] = [];
                    filter.groups.forEach((group, groupIndex) => {
                        if (group.defaultSelections) {
                            group.defaultSelections.forEach((value) => {
                                const option = group.options.find((opt) => opt.value === value);
                                if (option) {
                                    defaultGroupOptions.push({
                                        groupId: groupIndex,
                                        label: option.name,
                                        value: option.value,
                                    });
                                }
                            });
                        }
                    });
                    if (defaultGroupOptions.length > 0) {
                        initialSelections[filter.label] = defaultGroupOptions;
                    }
                    break;
                }
                case FilterType.USERS:
                case FilterType.TAGS: {
                    if (filter.defaultSelections && filter.defaultSelections.length > 0) {
                        initialSelections[filter.label] = filter.defaultSelections;
                    }
                    break;
                }
            }
        }
        return initialSelections;
    });

    const filteredData: RowData[] = useMemo(() => {
        if (!rowsData) {
            return [];
        } else if (!filters) {
            return rowsData;
        }

        return rowsData.filter((row) => {
            // Every filter must pass (AND logic between filters).
            return filters.every((filter) => {
                const selectedValues = filterSelections[filter.label];
                if (selectedValues === undefined || (Array.isArray(selectedValues) && selectedValues.length === 0)) {
                    return true;
                }

                return (() => {
                    switch (filter.type) {
                        case FilterType.SINGLE_PROPERTY: {
                            // Row passes if any of its values match any selected value (OR logic).
                            const rowValues = filter.getFilterableDataForRow(row);
                            if (filter.canSelectMultiple) {
                                const selected = selectedValues as CellValue[];
                                return rowValues.some((rowValue) => selected.includes(rowValue));
                            } else {
                                const selected = selectedValues as CellValue;
                                return rowValues.includes(selected);
                            }
                        }

                        case FilterType.MULTIPLE_PROPERTIES: {
                            const selectedGroupOptions = selectedValues as GroupOptionType[];
                            const selectionsByGroup = new Map<number, CellValue[]>();

                            selectedGroupOptions.forEach((opt) => {
                                const groupIndex = opt.groupId as number;
                                const existing = selectionsByGroup.get(groupIndex) ?? [];
                                existing.push(opt.value);
                                selectionsByGroup.set(groupIndex, existing);
                            });

                            // Row must match at least one selection from each group that has selections (OR logic within groups, AND logic between groups).
                            return Array.from(selectionsByGroup.entries()).every(([groupIndex, groupValues]) => {
                                const group = filter.groups[groupIndex];
                                const rowValues = group.getFilterableDataForRow(row);
                                return rowValues.some((rowValue) => groupValues.includes(rowValue));
                            });
                        }

                        case FilterType.USERS: {
                            const selectedUserIds = selectedValues as string[];
                            const rowUserIds = filter.getUserIdsForRow(row);
                            return rowUserIds.some((userId) => selectedUserIds.includes(userId));
                        }

                        case FilterType.TAGS: {
                            const selectedTagIds = selectedValues as string[];
                            const rowTagIds = filter.getTagIdsForRow(row);
                            return rowTagIds.some((tagId) => selectedTagIds.includes(tagId));
                        }
                    }
                })();
            });
        });
    }, [rowsData, filters, filterSelections]);

    const columnDefinitions = useMemo(() => columns.map(getColumnDefinitionFromColumnConfig), [columns]);

    // Validate columnSubsortOrder, if provided. This catches configuration errors as early as possible during development.
    if (columnSubsortOrder) {
        for (const columnName of columnSubsortOrder) {
            const column = columnDefinitions.find((c) => c.name === columnName);
            if (!column) {
                throw new Error(`"${columnName}" is not a valid column.`);
            }
            if (!column.sortComparator) {
                throw new Error(`"${columnName}" does not have a sortComparator and cannot be used for sorting.`);
            }
        }
    }

    // Sort filtered data using primary sort column, then tie-breaker columns for equal values.
    const sortedData: RowData[] = useMemo(() => {
        if (sortColumnName === null) {
            return filteredData;
        }

        const primarySortColumn = columnDefinitions.find((column) => column.name === sortColumnName)!;
        const primaryComparator = primarySortColumn.sortComparator ?? (() => 0);

        // Build ordered list of comparators: primary column first, then tie-breakers.
        const comparators: ((a: RowData, b: RowData) => number)[] = [];

        comparators.push((a, b) => {
            const result = undefinedComparator(primarySortColumn.getValue(a), primarySortColumn.getValue(b), (va, vb) => primaryComparator(va, vb, a, b));
            return sortDirection === 'ascending' ? result : -result;
        });

        const tieBreakColumns = columnSubsortOrder ? columnSubsortOrder.filter((name) => name !== sortColumnName).map((name) => columnDefinitions.find((c) => c.name === name)!) : columnDefinitions.filter((c) => c.name !== sortColumnName && c.sortComparator);

        for (const column of tieBreakColumns) {
            const columnComparator = column.sortComparator!;
            comparators.push((a, b) => {
                const result = undefinedComparator(column.getValue(a), column.getValue(b), (va, vb) => columnComparator(va, vb, a, b));
                return sortDirection === 'ascending' ? result : -result;
            });
        }

        const dataCopy = [...filteredData];
        sortSuccessively(dataCopy, comparators);
        return dataCopy;
    }, [columnDefinitions, filteredData, sortColumnName, sortDirection, columnSubsortOrder]);

    const handleFilterChange = (filterLabel: string, value: any): void => {
        setFilterSelections((prev) => ({
            ...prev,
            [filterLabel]: value,
        }));
    };

    // Handles column header clicks for sorting. Toggles direction if the current "sort by" column is clicked again, otherwise sorts by the new column in ascending order.
    const handleHeaderClick = (columnName: string): void => {
        if (columnName === sortColumnName) {
            setSortDirection((prev) => (prev === 'ascending' ? 'descending' : 'ascending'));
        } else {
            setSortColumnName(columnName);
            setSortDirection('ascending');
        }
    };

    const createHeaderCell = (column: ColumnDefinition<RowData, any>): JSX.Element => {
        const textColor = pageVariant === PAGE_VARIANT_DEFAULT ? 'blue' : 'darkGray';

        if (disableSorting || !column.sortComparator) {
            return (
                <TableCell key={column.name}>
                    <Text variant="Text4" color={textColor} noStyles toUppercase>
                        {column.name}
                    </Text>
                </TableCell>
            );
        }

        const isActive = sortColumnName === column.name;
        const sortDirectionAbbreviated = sortDirection === 'ascending' ? 'asc' : 'desc';
        return (
            <TableCell key={column.name} sortDirection={isActive ? sortDirectionAbbreviated : false}>
                <TableSortLabel active={isActive} direction={isActive ? sortDirectionAbbreviated : 'asc'} onClick={() => handleHeaderClick(column.name)}>
                    <Text variant="Text4" color={textColor} noStyles toUppercase>
                        {column.name}
                    </Text>
                </TableSortLabel>
            </TableCell>
        );
    };

    const headerStyle = pageVariant === PAGE_VARIANT_DEFAULT ? STANDARD_TABLE_HEADER_STYLE : DASHBOARD_TABLE_HEADER_STYLE;
    const colorTheme: ColorTheme = pageVariant === PAGE_VARIANT_DEFAULT ? 'light' : 'dark';

    if (!rowsData) {
        return (
            <div className={styles.tableContainer}>
                {filters.length > 0 && (
                    <div className={styles.filtersContainer}>
                        {filters.map((filter) => (
                            <div key={filter.label} className={filter.width === 'large' ? styles.filterLargeWidth : styles.filterStandardWidth}>
                                <Skeleton variant="rectangular" height={38} />
                            </div>
                        ))}
                    </div>
                )}
                <Skeleton variant="rounded" width={'100%'} height={300} />
            </div>
        );
    }

    return (
        <div className={styles.tableContainer}>
            {filters.length > 0 && (
                <div className={styles.filtersContainer}>
                    {filters.map((filter) => (
                        <Filter key={filter.label} filter={filter} selectedValue={filterSelections[filter.label]} onValueChange={(value) => handleFilterChange(filter.label, value)} colorTheme={colorTheme} />
                    ))}
                </div>
            )}
            <MUITable>
                <TableHead sx={headerStyle}>
                    <TableRow sx={{ '& th': { paddingTop: '8px', paddingBottom: '8px' } }}>
                        {getExpandableElementForRow && <TableCell />}
                        {columnDefinitions.map(createHeaderCell)}
                        {getOverflowMenuForRow && <TableCell />}
                    </TableRow>
                </TableHead>
                <MUITableBody>
                    {sortedData.map((data) =>
                        getExpandableElementForRow ? (
                            <ExpandableRow key={getUniqueIdForRow(data)} data={data} columns={columnDefinitions} pageVariant={pageVariant} getExpandableElementForRow={getExpandableElementForRow} getOverflowMenuForRow={getOverflowMenuForRow} />
                        ) : (
                            <TableRow key={getUniqueIdForRow(data)}>
                                {columnDefinitions.map((column, columnIndex) => {
                                    const value = column.getValue(data);
                                    return value === undefined ? <TableCell key={columnIndex} /> : <TableCell key={columnIndex}>{column.getTableCellContent(data, value, pageVariant)}</TableCell>;
                                })}
                                {getOverflowMenuForRow && <MUITableOverflowCell>{getOverflowMenuForRow(data)}</MUITableOverflowCell>}
                            </TableRow>
                        )
                    )}
                </MUITableBody>
            </MUITable>
        </div>
    );
};

interface ExpandableRowProps<RowData> {
    columns: ColumnDefinition<RowData, any>[];
    data: RowData;
    getExpandableElementForRow: (data: RowData) => JSX.Element;
    pageVariant: PageVariant;

    getOverflowMenuForRow?: (data: RowData) => JSX.Element;
}

/**
 * Renders a "main" row and a "details" row.
 */
const ExpandableRow = <RowData,>(props: ExpandableRowProps<RowData>): JSX.Element => {
    const { data, columns, getExpandableElementForRow, getOverflowMenuForRow, pageVariant } = props;
    const [open, setOpen] = useState(false);

    const directionStyle = open ? styles.expandIconDown : styles.expandIconRight;
    const colorStyle = pageVariant === 'dashboard' ? styles.expandIconWhite : styles.expandIconBlue;

    const expandableRowColSpan = getOverflowMenuForRow === undefined ? columns.length : columns.length + 1;

    return (
        <>
            <TableRow>
                <TableCell className={styles.parentCell}>
                    <button type="button" onClick={() => setOpen(!open)} className={`${styles.expandIcon} ${directionStyle} ${colorStyle}`}>
                        <FontAwesomeIcon icon={ICON_EXPAND_COLLAPSE} />
                    </button>
                </TableCell>
                {columns.map((column, columnIndex) => {
                    const value = column.getValue(data);
                    return (
                        <TableCell key={columnIndex} className={styles.parentCell}>
                            {value === undefined ? <></> : column.getTableCellContent(data, value, pageVariant)}
                        </TableCell>
                    );
                })}
                {getOverflowMenuForRow && <MUITableOverflowCell className={styles.parentCell}>{getOverflowMenuForRow(data)}</MUITableOverflowCell>}
            </TableRow>
            <TableRow>
                <TableCell className={styles.childCell} />
                <TableCell className={styles.childCell} colSpan={expandableRowColSpan}>
                    <Collapse in={open} timeout="auto">
                        {getExpandableElementForRow(data)}
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

/**
 * Sorts an array in place using multiple comparators for tie breaking.
 * Elements are first compared using the first comparator. If they are equal (comparator returns 0), the next comparator is used, and so on until a non-zero result is found or all comparators are exhausted.
 *
 * @typeParam RowData - The type of elements in the array.
 * @param data - The array to sort in place.
 * @param sortComparators - An ordered list of comparator functions. Earlier comparators take precedence.
 */
export const sortSuccessively = <RowData,>(data: RowData[], sortComparators: ((a: RowData, b: RowData) => number)[]): void => {
    data.sort((a, b) => {
        for (const comparator of sortComparators) {
            const result = comparator(a, b);
            // If the comparator returns a non-zero result, use it. Otherwise, try the next comparator.
            if (result !== 0) {
                return result;
            }
        }
        // All comparators returned 0, so the elements are considered equal.
        return 0;
    });
};
